import { ResourceClient, type HttpClient, type JobDTO } from "@dl/web-core-js/api";

export type ProductType = "SINGLE_ITEM" | "COMPONENT" | "BUNDLE";
export type ProductStatus = "AVAILABLE" | "PENDING_LAUNCH" | "PENDING_DISCONTINUED" | "DISCONTINUED";

/** Declared once so the grid badge and the editor dropdown cannot drift apart. */
export const PRODUCT_STATUSES: ProductStatus[] = [
  "AVAILABLE",
  "PENDING_LAUNCH",
  "PENDING_DISCONTINUED",
  "DISCONTINUED",
];

export const PRODUCT_TYPES: ProductType[] = ["SINGLE_ITEM", "COMPONENT", "BUNDLE"];

export const IMAGE_TYPES = [
  "WHITE_BACKGROUND",
  "LIFESTYLE",
  "DETAIL",
  "MACRO",
  "DIMENSIONS",
] as const;
/** Where the camera stands in an image. Read by the Shopify image rules only. */
export const IMAGE_ORIENTATIONS = ["FRONT", "ANGLED", "NA"] as const;
export type ImageOrientation = (typeof IMAGE_ORIENTATIONS)[number];

/** One row of the business order of image types (Settings › Products). */
export interface ImageTypeOrderDTO {
  id: number;
  image_type: string;
  sort_order: number;
}
export type ProductAcquisitionType = "FABRICATED" | "PURCHASED";

export type Country = "CANADA" | "USA";
export type Market = "CA" | "US";

export interface ProductMetadataDTO {
  sku: string;
  upc?: string | null;
  harmonized_code?: string | null;
  brand?: string | null;
  manufacturer?: string | null;
  country_of_origin?: Country | null;
  keywords?: string | null;
}

export interface ProductDescriptionDTO {
  language: string;
  product_name?: string | null;
  short_description?: string | null;
  long_description?: string | null;
  feature1?: string | null;
  feature2?: string | null;
  feature3?: string | null;
  feature4?: string | null;
  feature5?: string | null;
}

export interface ProductPricingDTO {
  cogs?: number | null;
  map?: number | null;
  msrp?: number | null;
}

/** Per-warehouse split of the same figures, keyed by warehouse id. */
export interface ProductKpiInventoryWarehouseDTO {
  quantity: number;
  reserved_quantity: number;
  in_order_quantity: number;
}

export interface ProductKpiInventoryDTO {
  quantity: number;
  reserved_quantity: number;
  in_order_quantity: number;
  planned_quantity: number;
  production_quantity: number;
  production_stage_quantity?: number;
  packaging_stage_quantity?: number;
  inventory_value: number;
  /** This SKU's share of total on-hand units, as a fraction (0–1). */
  percent_of_inventory_quantity?: number;
  percent_of_inventory_value?: number;
  turnover_12_weeks?: number;
  turnover_52_weeks?: number;
  first_stock_date?: string | null;
  first_stock_aging_days?: number | null;
  /**
   * Planning floor the production pipeline refuses to dip into, derived by
   * the KPI update from the safety-stock rules. Not a reservation: it is not
   * part of `netQuantity` or `availableQuantity`, and nothing but the pipeline
   * plans against it.
   */
  safety_stock_quantity?: number;
  warehouse_quantities?: Record<string, ProductKpiInventoryWarehouseDTO>;
}

/**
 * Every sales window the backend precomputes, split by the currency the
 * invoice was raised in.
 *
 * The split is the point: CAD and USD are separate columns on the wire and are
 * only summed for display, because the CA/US business is reported separately.
 * Unit totals are therefore always `cad + usd` — hence the `*Units` helpers
 * below rather than a single field.
 */
export interface ProductKpiSalesDTO {
  first_sale_date?: string | null;
  first_sale_aging_days?: number | null;
  last_sale_date?: string | null;
  last_sale_aging_days?: number | null;

  four_weeks_sales_cad?: number;
  four_weeks_sales_usd?: number;
  four_weeks_units_cad?: number;
  four_weeks_units_usd?: number;

  twelve_weeks_sales_cad?: number;
  twelve_weeks_sales_usd?: number;
  twelve_weeks_units_cad?: number;
  twelve_weeks_units_usd?: number;

  twenty_four_weeks_sales_cad?: number;
  twenty_four_weeks_sales_usd?: number;
  twenty_four_weeks_units_cad?: number;
  twenty_four_weeks_units_usd?: number;

  fifty_two_weeks_sales_cad?: number;
  fifty_two_weeks_sales_usd?: number;
  fifty_two_weeks_units_cad?: number;
  fifty_two_weeks_units_usd?: number;

  ytd_sales_cad: number;
  ytd_sales_usd: number;
  ytd_units_cad: number;
  ytd_units_usd: number;

  ytd_minus_1_sales_cad?: number;
  ytd_minus_1_sales_usd?: number;
  ytd_minus_1_units_cad?: number;
  ytd_minus_1_units_usd?: number;

  year_minus_1_sales_cad?: number;
  year_minus_1_sales_usd?: number;
  year_minus_1_units_cad?: number;
  year_minus_1_units_usd?: number;

  year_minus_2_sales_cad?: number;
  year_minus_2_sales_usd?: number;
  year_minus_2_units_cad?: number;
  year_minus_2_units_usd?: number;

  year_minus_3_sales_cad?: number;
  year_minus_3_sales_usd?: number;
  year_minus_3_units_cad?: number;
  year_minus_3_units_usd?: number;

  year_minus_4_sales_cad?: number;
  year_minus_4_sales_usd?: number;
  year_minus_4_units_cad?: number;
  year_minus_4_units_usd?: number;

  year_minus_5_sales_cad?: number;
  year_minus_5_sales_usd?: number;
  year_minus_5_units_cad?: number;
  year_minus_5_units_usd?: number;

  /** Exponentially-weighted weekly unit averages. */
  four_weeks_units_exp_avg?: number;
  twelve_weeks_units_exp_avg?: number;
  twenty_four_weeks_units_exp_avg?: number;
  twelve_months_units_exp_avg?: number;

  /**
   * Weekly units the planner and the products page use: the 4-week average
   * until the product has `velocity_blend_after_weeks` on sale, then the 4/12
   * blend (general settings). A single's units include those sold in bundles.
   */
  velocity?: number;
  velocity_ca?: number;
  velocity_us?: number;
  /** Complete weeks since the week of the first sale, that week included. */
  weeks_on_sale?: number;
  /**
   * Rank on the configured basis: singles among singles, bundles among
   * bundles, AVAILABLE and PENDING_LAUNCH only. Null for anything else.
   */
  sales_rank?: number | null;
  /** The same rank on Canadian-currency figures only: what floors a product on Canadian channels. */
  sales_rank_ca?: number | null;
  /** The same rank on US-currency figures only. */
  sales_rank_us?: number | null;
}

/** Window keys usable with `units()` / `salesCad()` / `salesUsd()`. */
export type SalesWindow =
  | "four_weeks"
  | "twelve_weeks"
  | "twenty_four_weeks"
  | "fifty_two_weeks"
  | "ytd"
  | "ytd_minus_1"
  | "year_minus_1"
  | "year_minus_2"
  | "year_minus_3"
  | "year_minus_4"
  | "year_minus_5";

/** `getXUnits()` on the Java DTO: the CAD and USD unit counts added together. */
export function units(sales: ProductKpiSalesDTO | null | undefined, window: SalesWindow): number {
  if (!sales) return 0;
  const record = sales as unknown as Record<string, number | undefined>;
  return (record[`${window}_units_cad`] ?? 0) + (record[`${window}_units_usd`] ?? 0);
}

export function salesCad(sales: ProductKpiSalesDTO | null | undefined, window: SalesWindow): number {
  if (!sales) return 0;
  return (sales as unknown as Record<string, number | undefined>)[`${window}_sales_cad`] ?? 0;
}

export function salesUsd(sales: ProductKpiSalesDTO | null | undefined, window: SalesWindow): number {
  if (!sales) return 0;
  return (sales as unknown as Record<string, number | undefined>)[`${window}_sales_usd`] ?? 0;
}

/**
 * A SIGM warehouse. `no` is the code the business uses — NEXCA is Canada,
 * NEXUS the US — and is what the inventory split is labelled by.
 */
/**
 * `GET /warehouse`.
 *
 * Note there are two `WarehouseDTO` classes in domain-nexpim — `dto/part` and
 * `dto/warehouse` — with different fields. This endpoint serves the second; the
 * port modelled the first, so `no` / `name1` / `default` were never on the wire
 * and every lookup through them silently found nothing.
 */
/**
 * What a warehouse is for. Only DISTRIBUTION holds sellable stock. SUBCONTRACTOR
 * is stock we own at a subcontractor (VIZU, DELTA), consumed when its work is
 * received.
 */
export type WarehouseKind = "DISTRIBUTION" | "MANUFACTURING" | "SERVICE" | "SUBCONTRACTOR" | "EXTERNAL";

export interface WarehouseDTO {
  id: number;
  /** The business identifier — NEXCA, NEXUS, MEGA, SERV. */
  code: string;
  /** The matching whs_no in nexera. Null for every kind but DISTRIBUTION. */
  nexera_whs_no?: string | null;
  name?: string | null;
  kind: WarehouseKind;
  is_active: boolean;
  /** Finished production lots can land here. */
  is_production_sink: boolean;
}

export class WarehouseClient extends ResourceClient<WarehouseDTO> {
  constructor(http: HttpClient) {
    super(http, "/warehouse");
  }
}

export class ProductMetafieldClient extends ResourceClient<ProductMetafieldDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/metafield");
  }

  byProduct(productId: number): Promise<ProductMetafieldDTO[]> {
    return this.http.get<ProductMetafieldDTO[]>(`/product/metafield/product/${productId}`);
  }
}

export interface NamedDTO {
  id: number;
  name?: string | null;
}

/** A file in S3. Images are served straight from `url`, not proxied. */
export interface S3ObjectDTO {
  bucket_name?: string | null;
  content_type?: string | null;
  file_name?: string | null;
  file_path?: string | null;
  url?: string | null;
  status?: string | null;
  created_at?: string | null;
}

export interface ProductImageDTO {
  id: number;
  product_id?: number;
  image_type?: string | null;
  image?: S3ObjectDTO | null;
  /** Pre-scaled variant. Preferred for grids — the full image can be megabytes. */
  thumbnail?: S3ObjectDTO | null;
  priority?: number;
  orientation?: ImageOrientation;
}

function usableUrl(object: S3ObjectDTO | null | undefined): string | null {
  // `??` alone is not enough here: the wire can carry an empty string, and a
  // DELETED object may keep a stale URL that will 403.
  if (!object?.url || object.url.trim() === "") return null;
  if (object.status && object.status !== "ACTIVE") return null;
  return object.url;
}

/** Thumbnail if usable, otherwise the full image. */
export function imageUrl(image: ProductImageDTO | null | undefined): string | null {
  return usableUrl(image?.thumbnail) ?? usableUrl(image?.image) ?? null;
}

/** The full-resolution URL, for detail views. */
export function fullImageUrl(image: ProductImageDTO | null | undefined): string | null {
  return usableUrl(image?.image) ?? usableUrl(image?.thumbnail) ?? null;
}

/**
 * The list-shaped product: a denormalised read model carrying pricing,
 * inventory and sales KPIs inline so the grid needs one call rather than one
 * per product.
 *
 * It is also the heaviest payload in the estate — the whole catalogue arrives
 * in a single response today, which is what server-side paging is meant to fix.
 */
export interface LazyProductDTO {
  id: number;
  uuid?: string;
  type: ProductType;
  status: ProductStatus;
  acquisition_type: ProductAcquisitionType;
  variation_group?: number | null;
  /** Operator opt-out: this SKU is left out of its group's Shopify listing. */
  shopify_excluded?: boolean;
  created_at?: string;
  designer?: NamedDTO | null;
  category?: NamedDTO | null;
  metadata?: ProductMetadataDTO | null;
  description_en?: ProductDescriptionDTO | null;
  pricing?: ProductPricingDTO | null;
  kpi_inventory?: ProductKpiInventoryDTO | null;
  kpi_sales?: ProductKpiSalesDTO | null;
  tags?: NamedDTO[];
  collections?: NamedDTO[];
  variations?: ProductVariationDTO[];
  image?: ProductImageDTO | null;
}

/** The product's velocity, computed by the KPI job: see {@link ProductKpiSalesDTO.velocity}. */
export function velocityOf(product: LazyProductDTO | null | undefined): number {
  return product?.kpi_sales?.velocity ?? 0;
}

/**
 * The rank as a badge reads it: "#3" for a single, "Bundle #3" for a bundle —
 * the two are ranked apart, so a bare "#3" on a bundle would claim a place
 * among the singles. Null when the product has no rank.
 */
export function rankLabel(
  product: Pick<LazyProductDTO, "type" | "kpi_sales"> | null | undefined,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string | null {
  const rank = product?.kpi_sales?.sales_rank;
  if (!rank) return null;
  return product?.type === "BUNDLE"
    ? t("product.rank.bundle", "Bundle #{{rank}}", { rank })
    : t("product.rank.single", "#{{rank}}", { rank });
}

/** Which sales a ranking is computed on: everything, or one market's own. */
export type RankScope = "GLOBAL" | "CA" | "US";
export const RANK_SCOPES: RankScope[] = ["GLOBAL", "CA", "US"];

/** A product's rank and weekly velocity in one scope (sales_rank / _ca / _us with velocity / _ca / _us). */
export function scopedRank(
  product: Pick<LazyProductDTO, "kpi_sales"> | null | undefined,
  scope: RankScope,
): { rank: number | null; velocity: number | null } {
  const sales = product?.kpi_sales;
  if (!sales) return { rank: null, velocity: null };
  switch (scope) {
    case "GLOBAL":
      return { rank: sales.sales_rank ?? null, velocity: sales.velocity ?? null };
    case "CA":
      return { rank: sales.sales_rank_ca ?? null, velocity: sales.velocity_ca ?? null };
    case "US":
      return { rank: sales.sales_rank_us ?? null, velocity: sales.velocity_us ?? null };
  }
}

/** "#12", or "Bundle #12" for a bundle (bundles are ranked among bundles); null when unranked. */
export function rankText(
  rank: number | null | undefined,
  type: ProductType | null | undefined,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!rank) return null;
  return type === "BUNDLE"
    ? t("product.rank.bundle", "Bundle #{{rank}}", { rank })
    : t("product.rank.single", "#{{rank}}", { rank });
}

/** The label of a ranking scope: Global, Canada, US. */
export function rankScopeLabel(scope: RankScope, t: (key: string, fallback: string) => string): string {
  switch (scope) {
    case "GLOBAL":
      return t("product.rank.scope.global", "Global");
    case "CA":
      return t("product.rank.scope.ca", "Canada");
    case "US":
      return t("product.rank.scope.us", "US");
  }
}

/** `getNetQuantity`: on hand minus reservations minus open order lines. */
export function netQuantity(product: LazyProductDTO | null | undefined): number {
  const inventory = product?.kpi_inventory;
  if (!inventory) return 0;
  return inventory.quantity - inventory.reserved_quantity - inventory.in_order_quantity;
}

/** `getAvailableQuantity`: on hand minus reservations, order lines still counted. */
export function availableQuantity(product: LazyProductDTO | null | undefined): number {
  const inventory = product?.kpi_inventory;
  if (!inventory) return 0;
  return inventory.quantity - inventory.reserved_quantity;
}

/** Net quantity held in one warehouse, for the CA/US split on the grid. */
export function warehouseNetQuantity(
  product: LazyProductDTO | null | undefined,
  warehouseId: number | null | undefined,
): number {
  if (warehouseId === null || warehouseId === undefined) return 0;
  const bucket = product?.kpi_inventory?.warehouse_quantities?.[String(warehouseId)];
  if (!bucket) return 0;
  return bucket.quantity - bucket.reserved_quantity - bucket.in_order_quantity;
}

/**
 * Weeks of cover left at the current velocity.
 *
 * 999 is the "sells nothing but has stock" sentinel the planner uses so those
 * rows sort to the bottom rather than dividing by zero.
 */
export function remainingWeeks(product: LazyProductDTO | null | undefined): number {
  const velocity = velocityOf(product);
  const net = netQuantity(product);
  if (velocity === 0 && net > 0) return 999;
  if (velocity === 0) return 0;
  return Math.max(Math.trunc(net / velocity), 0);
}

export class LazyProductClient extends ResourceClient<LazyProductDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/lazy");
  }

  byType(type: ProductType): Promise<LazyProductDTO[]> {
    return this.http.get<LazyProductDTO[]>(`/product/lazy/type/${type}`);
  }

  bySkus(skus: string[]): Promise<LazyProductDTO[]> {
    return this.http.post<LazyProductDTO[]>("/product/lazy/skus", skus);
  }
}

/**
 * Retailer feed formats.
 *
 * Every one is `POST /product/document/<slug>` with a body of product ids and a
 * binary response, so they differ only by slug and file type — hence a table
 * rather than a method each. `zip` marks the ones that answer with an archive
 * because they split by category.
 */
export interface ExportFormat {
  slug: string;
  labelKey: string;
  zip?: boolean;
}

export const PRODUCT_EXPORTS: ExportFormat[] = [
  { slug: "metadata", labelKey: "export.metadata" },
  { slug: "amazon", labelKey: "export.amazon", zip: true },
  { slug: "team-us", labelKey: "export.teamus" },
  { slug: "meubles-rd", labelKey: "export.meublesrd", zip: true },
  { slug: "tanguay", labelKey: "export.tanguay" },
  { slug: "tanguay-single", labelKey: "export.tanguay-single", zip: true },
  { slug: "bison/us", labelKey: "export.bison-us" },
  { slug: "bison/ca", labelKey: "export.bison-ca" },
  { slug: "linenchest", labelKey: "export.linenchest" },
  { slug: "rona", labelKey: "export.rona" },
  { slug: "the-brick", labelKey: "export.thebrick" },
  { slug: "simons", labelKey: "export.simons" },
];

/** Which values a sync may overwrite. */
export interface SigmFieldMask {
  name: boolean;
  upc: boolean;
  hs_code: boolean;
  measurements: boolean;
  variation: boolean;
  category: boolean;
}

/**
 * What a SIGM sync should do.
 *
 * Grouped the way the work divides: what may be created or updated, which
 * fields may be overwritten, and which lists may be replaced. The last group
 * is the destructive one and previously had no controls at all.
 */
export interface SigmSyncData {
  /** One or more SKUs, separated by commas, spaces or new lines. */
  sku: string;
  /** Report what would change without writing anything. */
  dry_run: boolean;

  with_name: boolean;
  with_upc: boolean;
  with_hs_code: boolean;
  with_measurements: boolean;
  with_variation: boolean;
  with_category: boolean;

  create_missing: boolean;
  update_existing: boolean;

  /** Replaces the product's whole package list. */
  with_packages: boolean;
  /** Replaces a bundle's items and quantities. */
  with_group: boolean;
  /** Replaces the package list of each item in a bundle. */
  with_child_packages: boolean;

  with_create_items: boolean;
  with_update_items: boolean;
  /** Null means bundle items use the same fields as the product. */
  item_fields?: SigmFieldMask | null;
}

export type SigmSyncOutcome = "CREATED" | "UPDATED" | "UNCHANGED" | "SKIPPED" | "FAILED";

export type SigmSyncFailure =
  | "INVALID_SKU"
  | "NOT_IN_SIGM"
  | "CREATE_DISABLED"
  | "SAVE_FAILED"
  | "CHILD_SAVE_FAILED";

export type SigmSyncChangeType =
  | "PRODUCT_CREATED"
  | "NAME"
  | "UPC"
  | "HS_CODE"
  | "MEASUREMENTS"
  | "VARIATION"
  | "CATEGORY"
  | "ACQUISITION_TYPE"
  | "PACKAGES_REPLACED"
  | "PACKAGES_NONE_IN_SIGM"
  | "COMPOSITION_REBUILT"
  | "COMPOSITION_INCOMPLETE"
  | "CHILD_CREATED"
  | "CHILD_UPDATED"
  | "CHILD_PACKAGES_REPLACED"
  | "CHILD_SKIPPED_BOX"
  | "CHILD_SKIPPED_NOT_IN_SIGM"
  | "CHILD_SKIPPED_CREATE_DISABLED"
  | "CHILD_SKIPPED_UPDATE_DISABLED";

/** One thing the sync did, or would do on a dry run. */
export interface SigmSyncChange {
  type: SigmSyncChangeType;
  /** The item a child-scoped change belongs to. */
  sku: string | null;
  /** How many, for the changes that replace a list. */
  count: number | null;
  warning: boolean;
}

/** What the sync did to one SKU. */
export interface SigmSyncResult {
  sku: string | null;
  outcome: SigmSyncOutcome;
  failure: SigmSyncFailure | null;
  dry_run: boolean;
  changes: SigmSyncChange[];
}

/**
 * The full product: everything the editor touches, where `LazyProductDTO` is
 * the trimmed read model the grid uses.
 */
export const ASSEMBLY_LEVELS = ["FULL_ASSEMBLY", "PARTIAL_ASSEMBLY", "NO_ASSEMBLY"] as const;
export const MATTRESS_SIZES = ["CRIB", "SINGLE", "TWIN", "FULL_DOUBLE", "QUEEN", "KING", "CALIFORNIA_KING"] as const;
export const COLOR_MAPS = ["BEIGE", "BLUE", "BLACK", "BROWN", "CLEAR", "GOLD", "GREEN", "GREY", "IVORY", "ORANGE", "PINK", "PURPLE", "RED", "SILVER", "TAN", "WHITE", "YELLOW", "MULTI"] as const;
export const SHAPES = ["OCTAGON", "OVAL", "RECTANGLE", "ROUND", "RUNNER", "SQUARE", "U_SHAPED", "L_SHAPED"] as const;
export const MOUNTING_TYPES = ["FLOATING", "STAND", "WALL_MOUNT"] as const;
export const BED_DESIGNS = ["STANDARD", "CANOPY", "MURPHY", "OPEN_FRAME"] as const;
export const DISTRESSING_TYPES = ["NO_DISTRESSING", "WOOD_SCORING_AND_RUB_THROUGH", "WORMHOLES", "DISTRESSED_METAL", "COAT_PAINT_DISTRESSING"] as const;
export const NATURAL_VARIATION_TYPES = ["NATURAL_VARIATION", "NATURAL_WOOD_GRAIN_COLOR"] as const;
export const SLAT_MATERIALS = ["MANUFACTURED_WOOD", "SOLID_AND_MANUFACTURED_WOOD", "SOLID_WOOD", "METAL"] as const;
export const APPROVED_USES = ["RESIDENTIAL_USE", "COMMERCIAL_USE", "RESIDENTIAL_AND_COMMERCIAL_USE"] as const;

export interface ProductAssemblyDataDTO {
  assembly_instructions?: S3ObjectDTO | null;
  care_instructions?: S3ObjectDTO | null;
  is_assembly_required?: boolean | null;
  assembly_level?: (typeof ASSEMBLY_LEVELS)[number] | null;
  estimated_assembly_time?: number | null;
  is_box_spring_required?: boolean | null;
  max_tv_size_in_inches?: number | null;
  mattress_size_compatibility?: (typeof MATTRESS_SIZES)[number] | null;
}

export interface ProductSupplementaryDataDTO {
  is_product_set?: boolean | null;
  made_to_order?: boolean | null;
  material?: string | null;
  finish?: string | null;
  style?: string | null;
  pattern?: string | null;
  color_map?: string | null;
  closure_type?: string | null;
  door_style?: string | null;
  headboard_design?: string | null;
  headboard_type?: string | null;
  frame_type?: string | null;
  shape?: string | null;
  mounting_type?: string | null;
  finished_back?: boolean | null;
  center_support_legs?: boolean | null;
  bed_design?: string | null;
  purposeful_distressing_type?: string | null;
  natural_variation_type?: string | null;
  slat_material?: string | null;
  upholstery_material?: string | null;
  upholstery_material_details?: string | null;
  headboard_included?: boolean | null;
  bed_storage_included?: boolean | null;
  box_spring_included?: boolean | null;
  trundle_bed_included?: boolean | null;
  number_of_compartments?: number | null;
  number_of_doors?: number | null;
  number_of_drawers?: number | null;
  number_of_shelves?: number | null;
  weight_capacity?: number | null;
}

export interface ProductCertificationDataDTO {
  warranty?: string | null;
  supplier_intended_and_approved_use?: string | null;
  cpsia_cautionary_statement?: string | null;
  carb2_compliance?: boolean | null;
  california_proposition_65_warning?: boolean | null;
  fsc_certification?: boolean | null;
  gols3_certification?: boolean | null;
  greenguard_certification?: boolean | null;
  greenguard_gold_certification?: boolean | null;
  grs_certification?: boolean | null;
  rcs_certification?: boolean | null;
  iso_14021_recycled_content_standard_certification?: boolean | null;
  certipur_us_certification?: boolean | null;
  gots5_certification?: boolean | null;
}

export interface ProductMeasurementsDTO {
  width_in_inches?: number | null;
  height_in_inches?: number | null;
  depth_in_inches?: number | null;
  weight_in_lbs?: number | null;
}

/**
 * What a product publishes to one sales channel. DISPATCHABLE sends what the
 * dispatch list can ship, FLOOR sends max(floor_quantity, dispatchable) — the
 * only mode with a quantity — and BLOCKED sends 0. Every product has a rule
 * on every channel; `is_listed: false` means the channel does not carry it.
 * `is_locked` means a person decided the rule: the daily inventory rule job
 * leaves it alone, except for the hard rules (components BLOCKED and unlisted,
 * DISCONTINUED / PENDING_LAUNCH BLOCKED, no FLOOR while PENDING_DISCONTINUED),
 * which the server also applies on save.
 */
export type InventoryRuleMode = "DISPATCHABLE" | "FLOOR" | "BLOCKED" | "DERIVED";
/** The modes a single item (or a component) can hold. */
export const INVENTORY_RULE_MODES: InventoryRuleMode[] = ["DISPATCHABLE", "FLOOR", "BLOCKED"];
/**
 * The modes a bundle can hold: DERIVED (its default — computed from its single
 * items on the same channel) or BLOCKED (send 0 whatever the items say).
 */
export const BUNDLE_RULE_MODES: InventoryRuleMode[] = ["DERIVED", "BLOCKED"];

export interface ProductInventoryRuleDTO {
  id: number;
  product_id?: number;
  sales_channel_id: number;
  mode: InventoryRuleMode;
  /** Set exactly when `mode` is FLOOR; the server rejects anything else. */
  floor_quantity?: number | null;
  /**
   * Ageing-out state of a DISCONTINUED product on this channel. Read-only on
   * the wire: the product save ignores both, only a delivered run, the relist
   * call and the inventory rule job move them.
   */
  discontinued_sends?: number;
  delisted?: boolean;
  is_listed: boolean;
  is_locked: boolean;
}

/** Where a rule change came from, as the history records it. */
export type InventoryRuleChangeSource = "MANUAL" | "BULK" | "IMPORT" | "AUTO" | "SYSTEM" | "SCRIPT" | "MIGRATION" | "BASELINE" | "SQL";

/** One change to a rule, written by a database trigger. Read-only. */
export interface ProductInventoryRuleHistoryDTO {
  id: number;
  changed_at: string;
  product_inventory_rule_id: number;
  product_id: number;
  sales_channel_id: number;
  operation: "INSERT" | "UPDATE" | "DELETE";
  source: InventoryRuleChangeSource;
  account_id?: number | null;
  account_username?: string | null;
  inventory_rule_run_id?: number | null;
  old_mode?: InventoryRuleMode | null;
  new_mode?: InventoryRuleMode | null;
  old_floor_quantity?: number | null;
  new_floor_quantity?: number | null;
  old_is_listed?: boolean | null;
  new_is_listed?: boolean | null;
  old_is_locked?: boolean | null;
  new_is_locked?: boolean | null;
  old_delisted?: boolean | null;
  new_delisted?: boolean | null;
}

/** One shipping carton; a product can ship in several. */
export interface ProductPackageDTO extends ProductMeasurementsDTO {
  id?: number;
  package_sku?: string | null;
  package_upc?: string | null;
}

/** One BOM line: a part this product is built from. */
export interface BomPartDTO {
  id: number;
  part?: {
    id?: number;
    no?: string;
    description1?: string;
    /** Per-warehouse on-hand; the BOM tab reads the MEGA (raw stock) row. */
    part_quantities?: Array<{ warehouse_no?: string | null; real_qty?: number | null }>;
  } | null;
  parent_part?: { no?: string } | null;
  budget_qty?: number | null;
  budget_unit?: string | null;
  cost?: number | null;
  avg_acquisition_time?: number | null;
}

/**
 * The BOM part's on-hand quantity in the raw-stock warehouse.
 *
 * MEGA is where components live before assembly, so it — not the total across
 * every warehouse — is what tells a planner whether a build can start.
 */
export function bomInventoryQty(line: BomPartDTO): number {
  const bucket = (line.part?.part_quantities ?? []).find(
    (entry) => entry.warehouse_no === "MEGA",
  );
  return bucket?.real_qty ?? 0;
}

/** A client's contracted price for this SKU, managed in the ERP. */
export interface PartClientPriceContractDTO {
  id: number;
  part_price?: number | null;
  part_discount?: number | null;
  sal_commission?: number | null;
  part?: { id?: number; no?: string | null; description1?: string | null } | null;
  /**
   * The SIGM client, which serialises with its ERP column names.
   *
   * The port originally declared this as `{ id, name, no, currency }`, none of
   * which are ever on the wire, so every lookup through it silently found
   * nothing and the tab rendered a dash in each cell. Same shape as
   * `SigmClientDTO` in the reporting client.
   */
  client?: {
    cli_id: number;
    cli_no?: string | null;
    cli_name1?: string | null;
    cli_currency?: string | null;
  } | null;
  /** Dates are `start_date`/`end_date` on the wire, not `date_from`/`date_to`. */
  price_contract?: {
    no?: string | null;
    description?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  } | null;
}

/** A dated cost breakdown; only one row is active at a time. */
export interface ProductCogsDTO {
  id: number;
  product_id?: number;
  produced?: number | null;
  production_labor_cogs?: number | null;
  production_overhead_cogs?: number | null;
  assembly_labor_cogs?: number | null;
  assembly_overhead_cogs?: number | null;
  material_cogs?: number | null;
  cogs?: number | null;
  date_from?: string | null;
  date_to?: string | null;
  is_active?: boolean;
  /** Set when a run is made the active cost, as the Vaadin Activate button did. */
  date_activated?: string | null;
}

export interface ProductNoteDTO {
  id: number;
  product_id?: number;
  user_account_id?: number | null;
  created_at?: string | null;
  note: string;
}

export type VariationType = "COLOR" | "SIZE";

export interface ProductVariationDTO {
  id: number;
  type?: VariationType | null;
  value?: string | null;
}

export interface MetafieldDTO {
  id: number;
  name: string;
}

export interface ProductMetafieldDTO {
  id: number;
  product_id: number;
  metafield: MetafieldDTO;
  value: string;
}

/**
 * One member of a bundle.
 *
 * The names are worth reading carefully: `master_product_id` is the bundle and
 * `product_id` is the member. The Java DTO renames the underlying
 * `sub_product_id` column to `product_id` on the wire, and the port followed
 * the column rather than the DTO — so it sent the bundle's own id as the member
 * and read the member back as undefined.
 */
export interface ProductGroupLineDTO {
  id: number;
  /** The bundle this line belongs to. */
  master_product_id?: number;
  /** The product the bundle contains. */
  product_id?: number;
  quantity: number;
}

export interface ProductDTO {
  id: number;
  uuid?: string;
  type: ProductType;
  status: ProductStatus;
  acquisition_type: ProductAcquisitionType;
  variation_group?: number | null;
  /** Operator opt-out: this SKU is left out of its group's Shopify listing. */
  shopify_excluded?: boolean;
  created_at?: string;
  designer?: NamedDTO | null;
  category?: NamedDTO | null;
  metadata?: ProductMetadataDTO | null;
  description_en?: ProductDescriptionDTO | null;
  description_fr?: ProductDescriptionDTO | null;
  pricing?: ProductPricingDTO | null;
  kpi_inventory?: ProductKpiInventoryDTO | null;
  kpi_sales?: ProductKpiSalesDTO | null;
  images?: ProductImageDTO[];
  tags?: NamedDTO[];
  collections?: NamedDTO[];
  assembly_data?: ProductAssemblyDataDTO | null;
  supplementary_data?: ProductSupplementaryDataDTO | null;
  certification_data?: ProductCertificationDataDTO | null;
  measurements?: ProductMeasurementsDTO | null;
  packages?: ProductPackageDTO[];
  variations?: ProductVariationDTO[];
  group_lines?: ProductGroupLineDTO[];
  /** Dispatch warehouses per market; empty list = fall back to market default. */
  source_warehouses?: Partial<Record<Market, number[]>>;
  notes?: ProductNoteDTO[];
  cogs?: ProductCogsDTO[];
  inventory_rules?: ProductInventoryRuleDTO[];
}

export type DispatchBulkMode = "SET" | "ADD" | "REMOVE";

export interface DispatchBulkRequest {
  product_ids: number[];
  /** A market left out keeps what each product has. */
  changes: Partial<Record<Market, { mode: DispatchBulkMode; warehouse_ids: number[] }>>;
}

export interface DispatchBulkResult {
  changed: number;
  unchanged: number;
  bundles_skipped: number;
}

export class ProductClient extends ResourceClient<ProductDTO> {
  constructor(http: HttpClient) {
    super(http, "/product");
  }

  /**
   * One dispatch change on many products, per market: set the list, add to it
   * or remove from it. Bundles are skipped (their dispatch is their items').
   */
  dispatchBulk(request: DispatchBulkRequest): Promise<DispatchBulkResult> {
    return this.http.post<DispatchBulkResult>("/product/dispatch/bulk", request);
  }

  /** Every product sharing a variation group, including this one. */
  byVariationGroup(group: number): Promise<ProductDTO[]> {
    return this.http.get<ProductDTO[]>(`/product/variation/${group}`);
  }

  /** Every bundle whose composition includes this product. */
  bundlesContaining(productId: number): Promise<ProductDTO[]> {
    return this.http.get<ProductDTO[]>(`/product/${productId}/bundles`);
  }

  /**
   * Product images and thumbnails stored without a file extension get the one of their type, in
   * storage and in their row. dryRun lists them; the answer is one line per file.
   */
  repairImageExtensions(dryRun: boolean): Promise<string[]> {
    return this.http.post<string[]>("/product/image/repair-extensions", null, { query: { dry_run: String(dryRun) } });
  }

  /** Named to avoid colliding with the generic `update` signature. */
  async updateProduct(product: ProductDTO): Promise<void> {
    await this.update(product);
  }

  /**
   * Recomputes every product's KPIs, then the production pipeline, in the
   * background — what the nightly cron does, on demand. Returns the job to
   * watch in the job center; the synchronous form of the call runs for
   * minutes and is only fit for a script that does not wait for the answer.
   */
  computeKpi(): Promise<JobDTO> {
    return this.http.post<JobDTO>("/product/kpi/compute", undefined, { query: { async: true } });
  }

  /**
   * Gives every product every tag it does not already carry. Pairs that
   * exist are left alone. Resolves to the ids of the products that changed.
   */
  addTags(productIds: number[], tagIds: number[]): Promise<number[]> {
    return this.http.post<number[]>("/product/tags/add", { product_ids: productIds, tag_ids: tagIds });
  }

  /** Strips the tags from the products; one without the tag is untouched. */
  removeTags(productIds: number[], tagIds: number[]): Promise<number[]> {
    return this.http.post<number[]>("/product/tags/remove", { product_ids: productIds, tag_ids: tagIds });
  }

  /** Generates a retailer feed for the selected products. */
  exportDocument(slug: string, productIds: number[]) {
    return this.http.job(`/product/document/${slug}`, { body: productIds });
  }

  /** Whole-catalogue inventory snapshot; takes no selection. */
  exportInventoryData() {
    return this.http.job("/product/document/inventory-data");
  }

  /**
   * Uploads a product feed. The endpoint reads the raw request body rather than
   * a multipart part, so the file goes up as octet-stream.
   *
   * @returns per-row problems; an empty list means everything imported
   */
  importFeed(file: File): Promise<string[]> {
    return this.http.request<string[]>("/product/import", {
      method: "POST",
      raw: file,
    });
  }

  /** BOM lines for a SKU. */
  bomByMasterPart(partNo: string): Promise<BomPartDTO[]> {
    return this.http.get<BomPartDTO[]>(`/bom/part/master/${encodeURIComponent(partNo)}`);
  }

  /** Contracted client prices for a SKU. */
  contractPrices(partNo: string): Promise<PartClientPriceContractDTO[]> {
    return this.http.get<PartClientPriceContractDTO[]>(
      `/contract/price/part-client/part/${encodeURIComponent(partNo)}`,
    );
  }

  /** All of one client's contracted prices — the pricing screen's join. */
  contractPricesByClient(clientId: number): Promise<PartClientPriceContractDTO[]> {
    return this.http.get<PartClientPriceContractDTO[]>(
      `/contract/price/part-client/client/${clientId}`,
    );
  }

  /** Uploads the assembly or care instructions PDF; replaces any existing one. */
  addInstructions(kind: "assembly-instructions" | "care-instructions", productId: number, file: File): Promise<ProductDTO> {
    return this.http.request<ProductDTO>(`/product/add/assembly/${kind}`, {
      method: "POST",
      raw: file,
      query: { product_id: productId },
    });
  }

  /** Removes the stored instructions PDF. */
  async deleteInstructions(kind: "assembly" | "care", productId: number): Promise<void> {
    await this.http.request<void>(`/product/delete/${kind}`, {
      method: "DELETE",
      query: { product_id: productId },
      responseType: "void",
    });
  }

  /**
   * Pulls products back from the SIGM ERP.
   *
   * Takes the whole SKU list in one call and answers with one result per SKU,
   * so a SKU missing from SIGM reports itself without abandoning the rest.
   */
  sigmSync(data: SigmSyncData): Promise<SigmSyncResult[]> {
    return this.http.post<SigmSyncResult[]>("/product/sigm-sync", data);
  }
}
